/**
 * Ranking experiments: run more than one set of weights at once, on slices of
 * the audience, and label the traffic so the difference can be graphed.
 *
 * The tuner (utils/ranking-config.ts) changes the ranking for everybody at
 * once, which is the right tool for "is this obviously better" and the wrong
 * one for "is this better". A weight change moves what buyers see; whether it
 * moves what they DO is a question only a comparison answers.
 *
 * ASSIGNMENT IS BY ANONYMOUS ID, IN TENTHS, AND IS NOT STORED.
 *
 * The bucket is a hash of the buyer's `anon_id` -- the same persistent id every
 * ClickHouse event already carries -- reduced to ten buckets of ten percent
 * each. Nothing is written anywhere: the same id always lands in the same
 * bucket, so the arm is recomputed rather than looked up, and there is no row
 * and no cookie on the hot path. That matters here because showcase data is
 * fetched client-side on every page.
 *
 * It is the id and not the address because an address is the wrong unit. Every
 * buyer behind one office NAT would share an arm, a phone would change arms
 * moving between wifi and cellular, and the last octet is not uniformly
 * distributed to begin with. The anonymous id has none of those problems, it
 * survives a network change, and it is already the join key the results are
 * grouped by. An address is kept only as a fallback for a request that arrives
 * without an id at all.
 *
 * It is the ANONYMOUS id rather than the session id on purpose. A session ends
 * with the visit, so bucketing on it would reassign a returning buyer and the
 * comparison would be between visits instead of between people.
 *
 * THE CONTROL ARM IS THE ABSENCE OF A VARIANT. Buckets no variant claims run
 * the saved live weights, so an experiment is always measured against what the
 * marketplace is actually doing, and switching everything off is one field.
 */

import { z } from 'zod';
import { RankingOverridesSchema } from './ranking-tunables';
import { anonymousIdFromCookieHeader } from './request-identity';
import { DEFAULT_TUNABLES, overlayTunables, resolveRankingWeights, type RankingWeights } from './elasticsearch/relevance-weights';

/** Ten buckets, so allocation moves in whole tenths of traffic. */
export const BUCKET_COUNT = 10;

/** Reserved label for traffic no variant claims. Never stored on a variant. */
export const CONTROL_LABEL = 'control';

/**
 * A variant is a partial override, not a whole configuration.
 *
 * Storing only what differs is what keeps an experiment meaningful while the
 * live weights are still being tuned: an experiment that pinned every knob
 * would silently freeze its arm against changes the control arm receives, and
 * the comparison would stop being about the one thing under test.
 */
/** A variant's hypothesis is a summary: a theory's (up to 1,000) is cut to this on its arm. */
export const VARIANT_HYPOTHESIS_MAX = 500;

export const RankingVariantSchema = z.object({
  /** Stable id, used as the analytics label. Lowercase, no spaces. */
  id: z.string().regex(/^[a-z0-9][a-z0-9_-]{0,38}[a-z0-9]$/, 'ids are lowercase, 2-40 chars, no spaces'),
  /** What a human calls it. */
  label: z.string().min(1).max(80),
  /** Why it exists and what would make it a win. Prose, for whoever reads the results. */
  hypothesis: z.string().max(VARIANT_HYPOTHESIS_MAX).default(''),
  /** Which tenths of traffic this arm takes. Digits 0-9, no duplicates. */
  buckets: z.array(z.number().int().min(0).max(BUCKET_COUNT - 1)).max(BUCKET_COUNT),
  /**
   * Only the knobs this arm changes. Everything else follows the live config.
   * NOT `RankingTunablesSchema.partial()` -- that keeps the defaults, and an arm
   * would silently override every knob it never set. See RankingOverridesSchema.
   */
  overrides: RankingOverridesSchema,
});

export type RankingVariant = z.infer<typeof RankingVariantSchema>;

/**
 * What a ranking test is judged on (owner, 2026-10-30: yes to a main-goal
 * picker). The results name it, mark its column and say which arm leads on it
 * against control. `better` is the direction that counts as a win.
 */
export const RANKING_GOALS = [
  { key: 'viewsPerSession', label: 'Views per session', better: 'up' },
  { key: 'clickThrough', label: 'Click-through', better: 'up' },
  { key: 'saveRate', label: 'Save rate', better: 'up' },
  { key: 'ordersPerBuyer', label: 'Orders per buyer', better: 'up' },
  { key: 'shownShare', label: 'Listings shown', better: 'up' },
  { key: 'unevenness', label: 'Unevenness', better: 'down' },
] as const;
export type RankingGoal = (typeof RANKING_GOALS)[number]['key'];
export const DEFAULT_RANKING_GOAL: RankingGoal = 'viewsPerSession';
export const RankingGoalSchema = z.enum(RANKING_GOALS.map((g) => g.key) as [RankingGoal, ...RankingGoal[]]);

export const RankingExperimentsSchema = z.object({
  /** One switch that returns every bucket to the live weights. */
  active: z.boolean().default(false),
  /** What the test is judged on. Absent (configs saved before 2026-10-30) reads as DEFAULT_RANKING_GOAL. */
  goal: RankingGoalSchema.optional(),
  variants: z.array(RankingVariantSchema).max(BUCKET_COUNT).default([]),
});

export type RankingExperiments = z.infer<typeof RankingExperimentsSchema>;

export const EMPTY_EXPERIMENTS: Readonly<RankingExperiments> = Object.freeze({
  active: false,
  goal: 'viewsPerSession',
  variants: [],
});

/**
 * FNV-1a, 32-bit. Small, dependency-free, and uniform enough that ten buckets
 * over UUIDs come out within a percent or two of even -- which is all that is
 * being asked of it. Deliberately NOT a cryptographic hash: this decides which
 * ranking someone sees, not anything anyone would try to forge.
 */
export function hashKey(key: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    hash ^= key.charCodeAt(i);
    // The FNV prime, as shifts, because a 32-bit multiply overflows a double.
    hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) >>> 0;
  }
  return hash >>> 0;
}

/**
 * Which tenth of the audience an anonymous id falls in.
 *
 * Returns null for an absent or empty id, and null means control: traffic we
 * cannot identify must never be swept silently into a treatment arm.
 */
export function bucketForKey(key: string | null | undefined): number | null {
  if (!key || typeof key !== 'string') return null;
  const trimmed = key.trim();
  if (!trimmed) return null;
  return hashKey(trimmed) % BUCKET_COUNT;
}

/**
 * Fallback for a request that carries no anonymous id -- a crawler, a
 * server-side caller, a browser with storage blocked.
 *
 * IPv4 uses the last decimal digit of the final octet; IPv6 uses the last
 * hexadecimal digit reduced mod 10, which folds a-f onto 0-5 and is therefore
 * not uniform. Both are worse than the id hash, which is why this is only ever
 * reached when there is no id.
 */
export function bucketForIp(ip: string | null | undefined): number | null {
  if (!ip || typeof ip !== 'string') return null;

  // `x-forwarded-for` is a list; the client is the first entry.
  let bare = ip.split(',')[0].trim();
  if (!bare) return null;

  // `[addr]:port` is the only form where a trailing `:digits` is a port on an
  // IPv6 address. Stripping `:digits` unconditionally would eat the last GROUP
  // of a bare IPv6 literal and silently bucket it on the wrong digit.
  const bracketed = bare.match(/^\[([^\]]+)\](?::\d+)?$/);
  if (bracketed) {
    bare = bracketed[1];
  } else if (/^\d{1,3}(?:\.\d{1,3}){3}:\d+$/.test(bare)) {
    bare = bare.replace(/:\d+$/, '');
  }

  const v4 = bare.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (v4) {
    if (v4.slice(1).some(part => Number(part) > 255)) return null;
    return Number(v4[4]) % BUCKET_COUNT;
  }

  // An IPv6 literal and nothing else. Anchored, so arbitrary prose containing
  // an "e" or an "f" is rejected rather than bucketed on a letter from a word.
  if (/^[0-9a-f:]+$/i.test(bare) && bare.includes(':')) {
    const digits = bare.replace(/:/g, '');
    if (!digits) return null;
    return parseInt(digits[digits.length - 1], 16) % BUCKET_COUNT;
  }

  return null;
}

/** Read the caller's address out of a request's headers. */
export function clientIpFrom(headers: Record<string, string | string[] | undefined>): string | null {
  const forwarded = headers['x-forwarded-for'];
  if (forwarded) return Array.isArray(forwarded) ? forwarded[0] : forwarded;
  const real = headers['x-real-ip'];
  if (real) return Array.isArray(real) ? real[0] : real;
  return null;
}

/**
 * The bucket for a request: the buyer's own id when it reached us, the address
 * only when it did not.
 *
 * The `sw_anonymous_id` cookie comes FIRST. proxy.ts sets it, and the event
 * ingest routes store every event under it, so it is the id the results group
 * by. Until 2026-10-20 only the home rows sent an id at all (the browser's
 * localStorage copy, which can differ from the cookie), and every other
 * surface bucketed by IP address: one buyer could get the arm on search and
 * control on the home rows, while their events said neither.
 */
export function resolveBucket(options: {
  anonymousId?: string | null;
  headers?: Record<string, string | string[] | undefined>;
}): number | null {
  const cookie = options.headers?.cookie;
  const fromCookie = bucketForKey(anonymousIdFromCookieHeader(Array.isArray(cookie) ? cookie.join('; ') : cookie));
  if (fromCookie !== null) return fromCookie;
  const fromId = bucketForKey(options.anonymousId);
  if (fromId !== null) return fromId;
  return bucketForIp(options.headers ? clientIpFrom(options.headers) : null);
}

export interface Assignment {
  /** The analytics label. `control` when no variant claims this bucket. */
  variantId: string;
  bucket: number | null;
  variant: RankingVariant | null;
}

export const CONTROL: Readonly<Assignment> = Object.freeze({
  variantId: CONTROL_LABEL,
  bucket: null,
  variant: null,
});

/**
 * Which arm this bucket is in. First claim wins, so an overlap -- which
 * validation rejects on save but a hand-edited document could still carry --
 * degrades to "the earlier variant" rather than to something non-deterministic.
 */
export function assignVariant(
  experiments: RankingExperiments | null | undefined,
  bucket: number | null
): Assignment {
  if (!experiments?.active || bucket === null) return CONTROL;

  for (const variant of experiments.variants) {
    if (variant.buckets.includes(bucket)) {
      return { variantId: variant.id, bucket, variant };
    }
  }
  return { ...CONTROL, bucket };
}

/**
 * The weights an arm actually runs: the live tunables with the variant's
 * overrides laid on top, resolved so the derived values follow.
 */
export function weightsForVariant(
  liveTunables: Partial<typeof DEFAULT_TUNABLES>,
  variant: RankingVariant | null | undefined
): RankingWeights {
  if (!variant) return resolveRankingWeights(liveTunables);
  // A partial override of a per-source map would otherwise silently discard
  // every live per-source value the arm was not trying to change.
  return resolveRankingWeights(overlayTunables(liveTunables, variant.overrides));
}

export interface ExperimentProblem {
  variantId: string;
  message: string;
}

/**
 * Everything wrong with a configuration, rather than the first thing.
 *
 * Bucket overlap is the one that matters: two arms claiming the same tenth
 * would make every measurement from both of them meaningless, and the failure
 * is completely invisible from the results.
 */
export function validateExperiments(experiments: RankingExperiments): ExperimentProblem[] {
  const problems: ExperimentProblem[] = [];
  const claimed = new Map<number, string>();
  const ids = new Set<string>();

  for (const variant of experiments.variants) {
    if (variant.id === CONTROL_LABEL) {
      problems.push({ variantId: variant.id, message: `"${CONTROL_LABEL}" is the label for unclaimed traffic and cannot be a variant` });
    }
    if (ids.has(variant.id)) {
      problems.push({ variantId: variant.id, message: 'duplicate variant id' });
    }
    ids.add(variant.id);

    if (variant.buckets.length === 0) {
      problems.push({ variantId: variant.id, message: 'takes no traffic' });
    }

    const seen = new Set<number>();
    for (const bucket of variant.buckets) {
      if (seen.has(bucket)) {
        problems.push({ variantId: variant.id, message: `bucket ${bucket} listed twice` });
        continue;
      }
      seen.add(bucket);

      const owner = claimed.get(bucket);
      if (owner) {
        problems.push({ variantId: variant.id, message: `bucket ${bucket} is already taken by "${owner}"` });
      } else {
        claimed.set(bucket, variant.id);
      }
    }
  }

  if (claimed.size >= BUCKET_COUNT) {
    problems.push({
      variantId: CONTROL_LABEL,
      message: 'every bucket is claimed, so there is no control arm to compare against',
    });
  }

  return problems;
}

/** Share of traffic each arm takes, control included. For the dash. */
export function trafficSplit(experiments: RankingExperiments): Array<{ variantId: string; buckets: number[]; share: number }> {
  const claimed = new Set<number>();
  const rows = experiments.variants.map(v => {
    const buckets = [...new Set(v.buckets)].sort((a, b) => a - b);
    buckets.forEach(b => claimed.add(b));
    return { variantId: v.id, buckets, share: buckets.length / BUCKET_COUNT };
  });

  const controlBuckets = Array.from({ length: BUCKET_COUNT }, (_v, i) => i).filter(b => !claimed.has(b));
  return [
    ...rows,
    { variantId: CONTROL_LABEL, buckets: controlBuckets, share: controlBuckets.length / BUCKET_COUNT },
  ];
}
